import {
  GetSecureInformationRequestsByCorporationItem,
  updateSecureInformationRequest,
  useCAFirms,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  MultiSelect,
} from '@commenda/kakashi';
import { useEffect, useState } from 'react';
import { KeyedMutator } from 'swr';
import { useNotification } from '../../hooks/useNotification';

const AssignSecureInformationRequestToCAFirmModal = ({
  isShown,
  toggle,
  id,
  mutate,
  row,
}: {
  isShown: boolean;
  toggle: () => void;
  id: string;
  row: GetSecureInformationRequestsByCorporationItem;
  mutate: KeyedMutator<unknown>;
}) => {
  const { setNotification } = useNotification();

  const [selectedCAFirms, setSelectedCAFirms] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: caFirms, error: caFirmsError } = useCAFirms();

  useEffect(() => {
    if (!caFirms) return;

    setSelectedCAFirms(row.assignedCAFirms.map((caFirm) => caFirm.id));
  }, [caFirms, row.assignedCAFirms]);

  if (!caFirms) {
    if (caFirmsError) console.error(caFirmsError);
    return (
      <Dialog open={isShown} onOpenChange={toggle}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign CA Firm</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[400px]">
            <LoadingIndicator size={64} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const onAssignCAFirms = async () => {
    setIsSubmitting(true);

    const res = await updateSecureInformationRequest(id, {
      caFirmIds: selectedCAFirms,
      corporationId: row.corporation.id,
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to assign CA firms',
      });
    }

    setIsSubmitting(false);
    mutate();
    toggle();
  };

  return (
    <Dialog open={isShown} onOpenChange={toggle}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign CA Firm</DialogTitle>
        </DialogHeader>
        <div className="p-6">
          <FieldWrapper>
            <Label>CA Firms</Label>
            <MultiSelect
              options={caFirms.map((caFirm) => ({
                label: caFirm.name,
                value: caFirm.id,
              }))}
              onValueChange={setSelectedCAFirms}
              defaultValue={selectedCAFirms}
              placeholder="Select CA Firms"
            />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button
            variant="primary"
            onClick={onAssignCAFirms}
            disabled={isSubmitting}
          >
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { AssignSecureInformationRequestToCAFirmModal };
